import * as Phaser from 'phaser';
import { createLogger } from '../utils/Logger';
import type { AimVectorProvider } from './WeaponTypes';

export interface InputVector {
  readonly x: number;
  readonly y: number;
}

export interface MobileInputSnapshot {
  readonly touchCapable: boolean;
  readonly enabled: boolean;
  readonly movementActive: boolean;
  readonly aimActive: boolean;
  readonly movementVector: InputVector;
  readonly aimVector: InputVector | null;
  readonly movementPointerId: number | null;
  readonly aimPointerId: number | null;
  readonly movementOrigin: InputVector | null;
  readonly movementPosition: InputVector | null;
  readonly aimOrigin: InputVector | null;
  readonly aimPosition: InputVector | null;
}

type PointerLike = Phaser.Input.Pointer & { id?: number; pointerId?: number };

const JOYSTICK_RADIUS = 72;
const MIN_VECTOR_DISTANCE = 8;

export class MobileInputController implements AimVectorProvider {
  private readonly logger = createLogger('MobileInputController');
  private readonly scene: Phaser.Scene;
  private readonly touchCapable: boolean;
  private enabled = true;
  private movementPointerId: number | null = null;
  private aimPointerId: number | null = null;
  private movementOrigin: InputVector | null = null;
  private movementPosition: InputVector | null = null;
  private aimOrigin: InputVector | null = null;
  private aimPosition: InputVector | null = null;
  private readonly unsubscribers: Array<() => void> = [];
  private lastMovementVector: InputVector = { x: 0, y: 0 };
  private lastAimVector: InputVector | null = null;

  constructor(scene: Phaser.Scene, touchCapable: boolean = detectTouchCapable()) {
    this.scene = scene;
    this.touchCapable = touchCapable;
  }

  public init(): void {
    if (!this.scene.input) {
      this.logger.warn('init:input-plugin-unavailable');
      return;
    }

    this.reservePointers();
    this.bindPointerEvent('pointerdown', this.handlePointerDown);
    this.bindPointerEvent('pointermove', this.handlePointerMove);
    this.bindPointerEvent('pointerup', this.handlePointerRelease);
    this.bindPointerEvent('pointerupoutside', this.handlePointerRelease);
    this.bindPointerEvent('pointercancel', this.handlePointerRelease);

    const blurHandler = () => this.clearPointers('window-blur');
    globalThis.window?.addEventListener?.('blur', blurHandler);
    this.unsubscribers.push(() => globalThis.window?.removeEventListener?.('blur', blurHandler));

    this.logger.info('init:complete', {
      touchCapable: this.touchCapable,
      enabled: this.enabled
    });
  }

  public setEnabled(enabled: boolean, source: string): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    this.logger.debug('setEnabled:changed', { enabled, source });
    if (!enabled) {
      this.clearPointers(`disabled:${source}`);
    }
  }

  public getMovementVector(): InputVector {
    if (!this.enabled || !this.movementOrigin || !this.movementPosition) {
      this.logMovementTransition({ x: 0, y: 0 });
      return { x: 0, y: 0 };
    }

    const vector = normalizeDragVector(this.movementOrigin, this.movementPosition);
    this.logMovementTransition(vector);
    return vector;
  }

  public getAimVector(): InputVector | null {
    if (!this.enabled || !this.aimOrigin || !this.aimPosition) {
      this.logAimTransition(null, 'inactive');
      return null;
    }

    const vector = normalizeDragVector(this.aimOrigin, this.aimPosition);
    if (Math.abs(vector.x) < Number.EPSILON && Math.abs(vector.y) < Number.EPSILON) {
      this.logAimTransition(null, 'dead-zone');
      return null;
    }

    this.logAimTransition(vector, 'touch');
    return vector;
  }

  public getSnapshot(): MobileInputSnapshot {
    return {
      touchCapable: this.touchCapable,
      enabled: this.enabled,
      movementActive: this.movementPointerId !== null,
      aimActive: this.aimPointerId !== null,
      movementVector: this.getMovementVector(),
      aimVector: this.getAimVector(),
      movementPointerId: this.movementPointerId,
      aimPointerId: this.aimPointerId,
      movementOrigin: this.movementOrigin,
      movementPosition: this.movementPosition,
      aimOrigin: this.aimOrigin,
      aimPosition: this.aimPosition
    };
  }

  public clearPointers(source: string): void {
    const hadPointers = this.movementPointerId !== null || this.aimPointerId !== null;
    this.movementPointerId = null;
    this.aimPointerId = null;
    this.movementOrigin = null;
    this.movementPosition = null;
    this.aimOrigin = null;
    this.aimPosition = null;
    this.lastMovementVector = { x: 0, y: 0 };
    this.lastAimVector = null;

    if (hadPointers) {
      this.logger.debug('clearPointers:complete', { source });
    }
  }

  public destroy(): void {
    this.clearPointers('destroy');
    this.unsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.logger.debug('destroy:complete');
  }

  private reservePointers(): void {
    const input = this.scene.input as Phaser.Input.InputPlugin & { pointers?: unknown[] };
    const currentCount = Array.isArray(input.pointers) ? input.pointers.length : 0;
    if (typeof input.addPointer === 'function' && currentCount < 4) {
      input.addPointer(4 - currentCount);
      this.logger.debug('reservePointers:added', { currentCount, targetCount: 4 });
    }
  }

  private bindPointerEvent(eventName: string, handler: (pointer: PointerLike) => void): void {
    const bound = handler.bind(this);
    this.scene.input.on(eventName, bound);
    this.unsubscribers.push(() => this.scene.input.off(eventName, bound));
  }

  private handlePointerDown(pointer: PointerLike): void {
    if (!this.enabled) {
      this.logger.debug('pointerdown:ignored', { reason: 'disabled', pointerId: getPointerId(pointer) });
      return;
    }

    if (!this.shouldAcceptPointer(pointer)) {
      this.logger.debug('pointerdown:ignored', { reason: 'not-touch', pointerId: getPointerId(pointer) });
      return;
    }

    const pointerId = getPointerId(pointer);
    const point = { x: pointer.x, y: pointer.y };
    const halfWidth = this.scene.scale.width / 2;
    const isMovementZone = pointer.x <= halfWidth;

    if (isMovementZone && this.movementPointerId === null) {
      this.movementPointerId = pointerId;
      this.movementOrigin = point;
      this.movementPosition = point;
      this.logger.debug('pointerdown:movement-assigned', { pointerId, point });
      return;
    }

    if (!isMovementZone && this.aimPointerId === null) {
      this.aimPointerId = pointerId;
      this.aimOrigin = point;
      this.aimPosition = point;
      this.logger.debug('pointerdown:aim-assigned', { pointerId, point });
      return;
    }

    this.logger.debug('pointerdown:ignored', {
      reason: 'zone-occupied',
      pointerId,
      isMovementZone,
      movementPointerId: this.movementPointerId,
      aimPointerId: this.aimPointerId
    });
  }

  private handlePointerMove(pointer: PointerLike): void {
    const pointerId = getPointerId(pointer);
    const point = { x: pointer.x, y: pointer.y };
    if (pointerId === this.movementPointerId) {
      this.movementPosition = point;
    } else if (pointerId === this.aimPointerId) {
      this.aimPosition = point;
    }
  }

  private handlePointerRelease(pointer: PointerLike): void {
    const pointerId = getPointerId(pointer);
    if (pointerId === this.movementPointerId) {
      this.movementPointerId = null;
      this.movementOrigin = null;
      this.movementPosition = null;
      this.lastMovementVector = { x: 0, y: 0 };
      this.logger.debug('pointerup:movement-released', { pointerId });
      return;
    }

    if (pointerId === this.aimPointerId) {
      this.aimPointerId = null;
      this.aimOrigin = null;
      this.aimPosition = null;
      this.lastAimVector = null;
      this.logger.debug('pointerup:aim-released', { pointerId });
    }
  }

  private shouldAcceptPointer(pointer: PointerLike): boolean {
    return this.touchCapable && pointer.wasTouch !== false;
  }

  private logMovementTransition(vector: InputVector): void {
    if (vectorsEqual(this.lastMovementVector, vector)) return;
    this.lastMovementVector = vector;
    this.logger.debug('movement-vector:changed', { vector });
  }

  private logAimTransition(vector: InputVector | null, source: string): void {
    if ((this.lastAimVector === null && vector === null) || (this.lastAimVector && vector && vectorsEqual(this.lastAimVector, vector))) {
      return;
    }

    this.lastAimVector = vector;
    this.logger.debug('aim-vector:changed', { source, vector });
  }
}

export function detectTouchCapable(): boolean {
  const nav = globalThis.navigator as Navigator | undefined;
  const win = globalThis.window as Window | undefined;
  const touchPointCount = Number(nav?.maxTouchPoints ?? 0);
  const hasTouchHardware = touchPointCount > 0 || Boolean(win && 'ontouchstart' in win);
  if (!hasTouchHardware) return false;

  const userAgent = nav?.userAgent ?? '';
  const userAgentData = nav as Navigator & { userAgentData?: { mobile?: boolean } };
  if (
    userAgentData.userAgentData?.mobile === true
    || /\b(Android|iPhone|iPad|iPod|Mobile|Windows Phone)\b/i.test(userAgent)
  ) {
    return true;
  }

  const hasCoarsePointer = matchesMedia('(pointer: coarse)');
  const hasNoHover = matchesMedia('(hover: none)');
  const platform = nav?.platform ?? '';
  const isIpadDesktopUserAgent = /\bMacintosh\b/i.test(userAgent) && touchPointCount > 1 && hasCoarsePointer && hasNoHover;
  const isDesktopPlatform = /\b(Win32|Win64|Windows|MacIntel|Linux x86_64|X11)\b/i.test(platform)
    || /\b(Windows NT|X11; Linux x86_64)\b/i.test(userAgent);
  if (isDesktopPlatform && !isIpadDesktopUserAgent) {
    return false;
  }

  return hasCoarsePointer && hasNoHover;
}

function matchesMedia(query: string): boolean {
  const matchMedia = globalThis.matchMedia ?? globalThis.window?.matchMedia;
  return typeof matchMedia === 'function' && matchMedia(query).matches;
}

function getPointerId(pointer: PointerLike): number {
  return Number(pointer.id ?? pointer.pointerId ?? 0);
}

function normalizeDragVector(origin: InputVector, position: InputVector): InputVector {
  const dx = position.x - origin.x;
  const dy = position.y - origin.y;
  const distance = Math.hypot(dx, dy);
  if (distance < MIN_VECTOR_DISTANCE) {
    return { x: 0, y: 0 };
  }

  const magnitude = Math.min(1, distance / JOYSTICK_RADIUS);
  return {
    x: (dx / distance) * magnitude,
    y: (dy / distance) * magnitude
  };
}

function vectorsEqual(left: InputVector, right: InputVector): boolean {
  return Math.abs(left.x - right.x) < 0.001 && Math.abs(left.y - right.y) < 0.001;
}
